import { UserInfo } from "../../application/UserProfile";
import {
  FormSubmitArgs,
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../records/forms/formView";
import { RecordDataSource } from "../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../records/grids/columns/columnDefinitionService";
import {
  ColumnDefinitionSetServiceProvider,
  vsProviderName as ColumnDefinitionSetServiceProviderName,
} from "../../records/grids/columns/columnDefinitionSetService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../records/views/recordGridView";
import fieldHelpers from "../../ui-formly/fieldHelpers";

export const vsObjectName = "user";

userObjectConfig.$inject = [
  RecordGridViewServiceProviderName,
  FormViewServiceProviderName,
  ColumnDefinitionSetServiceProviderName,
];
export function userObjectConfig(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  formViewServiceProvider: FormViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  configureGrid(
    recordGridViewServiceProvider,
    columnDefinitionSetServiceProvider
  );
  configureForm(formViewServiceProvider);
}

function configureForm(formViewServiceProvider: FormViewServiceProvider) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, [
    FormViewServiceName,
    (formViewService: FormViewService): FormViewConfig => {
      return {
        fields: [
          ...fieldHelpers.columnLayout(
            [
              fieldHelpers.simpleStringField({
                key: "UserName",
                label: "Username",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: "EmployeeId",
                label: "Employee Id",
              }),
              fieldHelpers.simpleStringField({
                key: "Email",
                label: "Email",
              }),
              fieldHelpers.simpleBoolField({
                key: "UseJobTitleForRoles",
                label: "Sync roles to job title",
                defaultValue: false,
              }),
              fieldHelpers.simpleStringField({
                key: "FirstName",
                label: "First Name",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: "LastName",
                label: "Last Name",
                required: true,
              }),
              fieldHelpers.simpleStringField({
                key: "JobTitle",
                label: "Job Title",
              }),
              fieldHelpers.simpleStringField({
                key: "JobCode",
                label: "Job Code",
              }),
            ],
            4
          ),
        ],
        name: vsObjectName,
        title: (model: UserInfo) => `Edit User ${model.UserName}`,
        submitForm: (args: FormSubmitArgs) => {
          const model = <UserInfo>args.model;
          const isExistingUser = !!model.UserAuthId;
          const url = `/api/user${
            isExistingUser ? `/${model.UserAuthId}` : ""
          }`;
          const method = isExistingUser ? "put" : "post";

          return formViewService.submitForm<UserInfo>({
            formObject: args.model,
            formViewController: args.formViewController,
            url,
            method,
          });
        },
      };
    },
  ]);
}

function configureGrid(
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  columnDefinitionSetServiceProvider: ColumnDefinitionSetServiceProvider
) {
  columnDefinitionSetServiceProvider.addColumnDefinitionSet(vsObjectName, [
    ColumnDefinitionServiceName,
    (columnDefinitionService: ColumnDefinitionService) => [
      columnDefinitionService.getNumberColDef({
        field: "UserAuthId",
        header: "User Id",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "UserName",
        header: "Username",
      }),
      columnDefinitionService.getStringColDef({
        field: "Email",
      }),
      columnDefinitionService.getStringColDef({
        field: "FirstName",
      }),
      columnDefinitionService.getStringColDef({
        field: "LastName",
      }),
      columnDefinitionService.getNumberColDef({
        field: "InvalidLoginAttempts",
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "LastLoginAttempt",
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "LockedDate",
      }),
    ],
  ]);

  recordGridViewServiceProvider.addGridConfiguration(vsObjectName, [
    ServerUtilityName,
    (serverUtility: ServerUtility): GridConfiguration => {
      return {
        columnDefinitionSet: vsObjectName,
        defaultSort: "LastName,FirstName",
        gridDataSource: new RecordDataSource(serverUtility, "/api/user/detail"),
        nameSingular: "User",
        showAddButton: true,
        showAddButtonPermission: "userModify",
      };
    },
  ]);
}
